import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, Trash2, Image as ImageIcon, ChevronLeft, Loader2, Save, X,
  Pencil, BookOpen, Upload, Crop,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ReferenceItemPhotoCropper from '../../components/ReferenceItemPhotoCropper';
import {
  listReferenceBrands, createReferenceBrand, deleteReferenceBrand,
  listReferenceItems, createReferenceItem, updateReferenceItem,
  deleteReferenceItem, uploadReferenceItemPhoto,
  fetchReferenceItemPhotoBlobUrl,
} from '../../services/api';

// Tag presets per kind — drives the form fields shown on Add/Edit so
// engineers don't have to remember which tags belong to which kind.
const KIND_FIELDS = {
  breaker: [
    { key: 'bsEn', label: 'BS (EN)', placeholder: 'e.g. 60898 / 61009', type: 'text' },
    { key: 'mcbType', label: 'Type', placeholder: 'B / C / D', type: 'text' },
    { key: 'ratingA', label: 'Rating (A)', placeholder: 'e.g. 32', type: 'number' },
    { key: 'breakingCapacityKa', label: 'Breaking Capacity (kA)', placeholder: 'e.g. 6', type: 'number' },
    { key: 'maxZs', label: 'Max permitted Zs (Ω)', placeholder: 'e.g. 3.56', type: 'number' },
    { key: 'ratedOperatingMa', label: 'Rated Operating Current (mA) — RCDs only', placeholder: 'e.g. 30', type: 'number' },
    { key: 'isThreePole', label: '3-pole breaker (three-phase)', type: 'checkbox' },
  ],
  board: [
    { key: 'phases', label: 'Phases', placeholder: '1 or 3', type: 'number' },
    { key: 'ways', label: 'Number of ways', placeholder: 'e.g. 6', type: 'number' },
  ],
  isolator: [
    { key: 'bsEn', label: 'BS (EN)', placeholder: 'e.g. 60947', type: 'text' },
    { key: 'poles', label: 'Number of Poles', placeholder: 'SP / DP / TP / TPN', type: 'text' },
    { key: 'ratingA', label: 'Rating (A)', placeholder: 'e.g. 100', type: 'number' },
  ],
};

const KIND_LABELS = { breaker: 'Breaker', board: 'Board', isolator: 'Isolator' };

const ItemPhotoThumb = ({ itemId, hasPhoto }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!hasPhoto || !itemId) return;
    let cancelled = false;
    let blob = null;
    fetchReferenceItemPhotoBlobUrl(itemId).then(u => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      blob = u;
      setUrl(u);
    }).catch(() => {});
    return () => { cancelled = true; if (blob) URL.revokeObjectURL(blob); };
  }, [itemId, hasPhoto]);
  if (!hasPhoto) {
    return (
      <div className="h-12 w-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">
        <ImageIcon className="h-5 w-5" />
      </div>
    );
  }
  return url ? (
    <img src={url} alt="" className="h-12 w-12 rounded border border-slate-200 object-cover bg-slate-50" />
  ) : (
    <div className="h-12 w-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
    </div>
  );
};

const ItemEditDialog = ({ open, onOpenChange, brandId, initial, onSaved }) => {
  const fileRef = useRef(null);
  const [form, setForm] = useState(() => initial || { kind: 'breaker', name: '' });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  // Bumping this forces ItemPhotoThumb to remount with a fresh blob URL
  // after a crop save replaces the stored bytes server-side.
  const [thumbVersion, setThumbVersion] = useState(0);

  useEffect(() => { setForm(initial || { kind: 'breaker', name: '' }); setPhotoFile(null); }, [initial, open]);

  // Local preview for the chosen photo before it's uploaded.
  useEffect(() => {
    if (!photoFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(photoFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const isEdit = !!initial?.id;
  const fields = KIND_FIELDS[form.kind] || [];

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      // Strip empty strings → null so the backend stores `None` rather
      // than '', which would show up in the AI prompt as a useless
      // 'KEY=' caption.
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      // Cast numeric fields. The HTML number input gives strings.
      ['ratingA', 'breakingCapacityKa', 'maxZs', 'ratedOperatingMa', 'phases', 'ways'].forEach(k => {
        if (payload[k] !== null && payload[k] !== undefined && payload[k] !== '') {
          const n = parseFloat(payload[k]);
          payload[k] = Number.isFinite(n) ? n : null;
        }
      });
      let saved;
      if (isEdit) {
        saved = await updateReferenceItem(initial.id, payload);
      } else {
        saved = await createReferenceItem(brandId, payload);
      }
      if (photoFile) {
        const heicSuffix = /\.(heic|heif)$/i.test(photoFile.name || '');
        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
        if (allowed.includes(photoFile.type) || heicSuffix) {
          saved = await uploadReferenceItemPhoto(saved.id, photoFile);
        } else {
          toast.error(`${photoFile.name}: only JPG/PNG/WEBP/HEIC supported`);
        }
      }
      toast.success(isEdit ? 'Item saved' : 'Item added');
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit reference item' : 'Add reference item'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div>
              <Label className="text-xs">Kind</Label>
              <Select value={form.kind} onValueChange={v => setForm(f => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="ref-item-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breaker">Breaker (MCB / RCBO / RCD)</SelectItem>
                  <SelectItem value="board">Distribution board</SelectItem>
                  <SelectItem value="isolator">Isolator / main switch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={form.name || ''}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={form.kind === 'board' ? 'e.g. Schneider 6-way TPN' : form.kind === 'isolator' ? 'e.g. Schneider 100A DP' : 'e.g. Schneider C10 60898'}
              data-testid="ref-item-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {fields.map(f => {
              if (f.type === 'checkbox') {
                return (
                  <div key={f.key} className="col-span-2 flex items-center gap-2 mt-1">
                    <Checkbox
                      id={`ref-item-${f.key}`}
                      checked={!!form[f.key]}
                      onCheckedChange={v => setForm(s => ({ ...s, [f.key]: !!v }))}
                      data-testid={`ref-item-${f.key}-input`}
                    />
                    <Label htmlFor={`ref-item-${f.key}`} className="text-xs cursor-pointer select-none">
                      {f.label}
                    </Label>
                  </div>
                );
              }
              return (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input
                    type={f.type}
                    step={f.type === 'number' ? 'any' : undefined}
                    value={form[f.key] ?? ''}
                    onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    data-testid={`ref-item-${f.key}-input`}
                  />
                </div>
              );
            })}
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input
              value={form.notes || ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional — e.g. 'Older yellow-handle revision'"
            />
          </div>
          <div>
            <Label className="text-xs">Reference photo</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                className="hidden"
                onChange={e => setPhotoFile(e.target.files?.[0] || null)}
              />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-8 gap-1" data-testid="ref-item-photo-pick-btn">
                <Upload className="h-3 w-3" />
                {photoFile ? 'Change photo' : (initial?.hasPhoto ? 'Replace photo' : 'Add photo')}
              </Button>
              {initial?.hasPhoto && !photoFile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCropOpen(true)}
                  className="h-8 gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  title="Adjust the photo's crop area"
                  data-testid="ref-item-photo-edit-crop-btn"
                >
                  <Crop className="h-3 w-3" />
                  Edit crop
                </Button>
              )}
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-12 w-12 object-cover rounded border border-slate-200" />
              ) : initial?.hasPhoto ? (
                <button
                  type="button"
                  onClick={() => setCropOpen(true)}
                  className="rounded border border-slate-200 hover:border-emerald-400 hover:ring-2 hover:ring-emerald-100 transition-all"
                  title="Click to view + edit crop"
                  data-testid="ref-item-photo-thumb-btn"
                >
                  <ItemPhotoThumb key={thumbVersion} itemId={initial.id} hasPhoto={true} />
                </button>
              ) : null}
              {photoFile && (
                <button onClick={() => setPhotoFile(null)} className="text-xs text-slate-500 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ref-item-save-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Save className="h-3 w-3 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
      {initial?.id && (
        <ReferenceItemPhotoCropper
          open={cropOpen}
          onOpenChange={setCropOpen}
          itemId={initial.id}
          onApplyTags={(tags) => {
            // Merge AI-extracted tags into the form fields. Only
            // overwrite when the AI returned a non-empty value so we
            // don't blow away values the engineer typed manually.
            setForm(s => {
              const next = { ...s };
              if (tags.bsEn) next.bsEn = tags.bsEn;
              if (tags.mcbType) next.mcbType = tags.mcbType;
              if (tags.ratingA != null) next.ratingA = tags.ratingA;
              if (tags.ratedOperatingMa != null) next.ratedOperatingMa = tags.ratedOperatingMa;
              if (typeof tags.isThreePole === 'boolean') next.isThreePole = tags.isThreePole;
              return next;
            });
          }}
          onSaved={(updated) => {
            setThumbVersion(v => v + 1);
            onSaved?.(updated || initial);
          }}
        />
      )}
    </Dialog>
  );
};

const ReferenceLibrary = () => {
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [items, setItems] = useState([]);
  const [filterKind, setFilterKind] = useState('all');
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [editing, setEditing] = useState(null); // { open, initial }

  const reloadBrands = async () => {
    setLoadingBrands(true);
    try {
      const list = await listReferenceBrands();
      setBrands(list);
      if (list.length && !activeBrandId) setActiveBrandId(list[0].id);
    } finally {
      setLoadingBrands(false);
    }
  };

  const reloadItems = async (brandId) => {
    if (!brandId) { setItems([]); return; }
    setLoadingItems(true);
    try {
      setItems(await listReferenceItems(brandId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load items');
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => { reloadBrands(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { reloadItems(activeBrandId); /* eslint-disable-next-line */ }, [activeBrandId]);

  const activeBrand = useMemo(() => brands.find(b => b.id === activeBrandId), [brands, activeBrandId]);
  const visibleItems = useMemo(() => {
    if (filterKind === 'all') return items;
    return items.filter(i => i.kind === filterKind);
  }, [items, filterKind]);

  const addBrand = async () => {
    const name = newBrand.trim();
    if (!name) return;
    try {
      const b = await createReferenceBrand(name);
      setNewBrand('');
      await reloadBrands();
      setActiveBrandId(b.id);
      toast.success(`Brand "${b.name}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add brand');
    }
  };

  const removeBrand = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}" and all its reference items? This cannot be undone.`)) return;
    try {
      const res = await deleteReferenceBrand(b.id);
      toast.success(`Deleted "${b.name}" and ${res.deletedItems} item(s)`);
      if (activeBrandId === b.id) setActiveBrandId(null);
      await reloadBrands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete brand');
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Delete reference item "${it.name}"?`)) return;
    try {
      await deleteReferenceItem(it.id);
      toast.success('Item deleted');
      reloadItems(activeBrandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete item');
    }
  };

  return (
    <div className="space-y-4" data-testid="reference-library-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/certificates" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900 mb-1">
            <ChevronLeft className="h-3 w-3" />
            Back to certificates
          </Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-600" />
            Reference Library
          </h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Manually-tagged photos of breakers, boards, and isolators. When you select a brand on the AI Extract screen, every photo'd item under that brand is sent to the model as a confirmed example so its readings on the site photo are anchored to the brand's actual visual fingerprint.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Brands sidebar */}
        <aside className="col-span-12 md:col-span-3 bg-white border border-slate-200 rounded p-3 space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1">Brands</div>
          <div className="flex gap-2">
            <Input
              value={newBrand}
              onChange={e => setNewBrand(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addBrand()}
              placeholder="New brand…"
              className="h-8 text-sm"
              data-testid="ref-add-brand-input"
            />
            <Button size="sm" onClick={addBrand} disabled={!newBrand.trim()} className="h-8" data-testid="ref-add-brand-btn">
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          {loadingBrands ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          ) : brands.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-2">No brands yet. Add Schneider, MK, Hager, Crabtree…</p>
          ) : (
            <ul className="space-y-1" data-testid="ref-brand-list">
              {brands.map(b => {
                const c = b.itemCounts || {};
                const total = (c.breaker || 0) + (c.board || 0) + (c.isolator || 0);
                return (
                  <li key={b.id} className="group flex items-center gap-1">
                    <button
                      onClick={() => setActiveBrandId(b.id)}
                      className={`flex-1 text-left px-2 py-1.5 rounded text-sm transition-colors ${
                        activeBrandId === b.id
                          ? 'bg-emerald-50 text-emerald-900 font-medium'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      data-testid={`ref-brand-${b.slug}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate">{b.name}</span>
                        <span className="text-[10px] text-slate-400 tabular-nums">{total}</span>
                      </div>
                    </button>
                    <button
                      onClick={() => removeBrand(b)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title={`Delete ${b.name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Items table */}
        <main className="col-span-12 md:col-span-9 bg-white border border-slate-200 rounded p-3 space-y-3">
          {!activeBrand ? (
            <div className="text-center py-12 text-slate-400">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Pick a brand on the left to manage its reference items.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-lg font-semibold">{activeBrand.name}</h2>
                  <p className="text-xs text-slate-500">
                    {items.length} item{items.length === 1 ? '' : 's'} ·{' '}
                    {items.filter(i => i.hasPhoto).length} with photos
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={filterKind} onValueChange={setFilterKind}>
                    <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All kinds</SelectItem>
                      <SelectItem value="breaker">Breakers</SelectItem>
                      <SelectItem value="board">Boards</SelectItem>
                      <SelectItem value="isolator">Isolators</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={() => setEditing({ open: true, initial: null })}
                    data-testid="ref-add-item-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add item
                  </Button>
                </div>
              </div>

              {loadingItems ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : visibleItems.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-6 text-center">
                  No items{filterKind !== 'all' ? ` of kind '${filterKind}'` : ''} yet — add one to start building the reference set.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase">
                    <tr>
                      {['', 'Name', 'Kind', 'Tags', ''].map(h => (
                        <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleItems.map(it => (
                      <tr key={it.id} className="hover:bg-slate-50 group">
                        <td className="px-2 py-2 w-14"><ItemPhotoThumb itemId={it.id} hasPhoto={it.hasPhoto} /></td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-slate-900">{it.name}</div>
                          {it.notes && <div className="text-[11px] text-slate-500 italic truncate max-w-[280px]">{it.notes}</div>}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{KIND_LABELS[it.kind]}</span>
                        </td>
                        <td className="px-2 py-2 text-xs text-slate-600">
                          <div className="flex flex-wrap gap-1">
                            {it.bsEn && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">BS EN {it.bsEn}</span>}
                            {it.mcbType && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Type {it.mcbType}</span>}
                            {it.ratingA != null && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{it.ratingA}A</span>}
                            {it.breakingCapacityKa != null && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{it.breakingCapacityKa}kA</span>}
                            {it.maxZs != null && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Zs {it.maxZs}Ω</span>}
                            {it.ratedOperatingMa != null && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">{it.ratedOperatingMa}mA</span>}
                            {it.isThreePole && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">3-pole</span>}
                            {it.phases != null && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{it.phases}-ph</span>}
                            {it.ways != null && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{it.ways}-way</span>}
                            {it.poles && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{it.poles}</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2 w-20 text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing({ open: true, initial: it })}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => removeItem(it)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </main>
      </div>

      {editing && (
        <ItemEditDialog
          open={editing.open}
          onOpenChange={open => setEditing(prev => ({ ...prev, open }))}
          brandId={activeBrandId}
          initial={editing.initial}
          onSaved={() => reloadItems(activeBrandId)}
        />
      )}
    </div>
  );
};

export default ReferenceLibrary;
